import { useState, useEffect } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTeamAnalytics } from "@/hooks/use-analytics-sync";
import { useExecutiveTeamUtilization } from "@/hooks/use-executive-demo-data";
import { RealtimeStatusIndicator } from "@/components/realtime/realtime-status-indicator";

const COLORS = ["hsl(var(--brand-primary))", "hsl(var(--brand-secondary))"];

interface TeamUtilizationProps {
  demoMode?: boolean;
}

export const TeamUtilization: React.FC<TeamUtilizationProps> = ({ demoMode = true }) => {
  const [selectedPeriod, setSelectedPeriod] = useState("week");
  const { data: demoData, isLoading: demoLoading } = useExecutiveTeamUtilization();
  const { data: teamAnalytics, isLoading: realLoading, refresh } = useTeamAnalytics(undefined, 7);
  
  const isLoading = demoMode ? demoLoading : realLoading;

  // Calculate utilization from demo or real team analytics
  const getChartData = () => {
    // Demo mode: calculate from demo data
    if (demoMode && demoData) {
      const avgUtilization = demoData.reduce((sum, member) => sum + member.utilization, 0) / demoData.length;
      return [
        { name: "Utilized", value: Math.round(avgUtilization) },
        { name: "Available", value: Math.round(100 - avgUtilization) },
      ];
    }

    // Real mode: calculate from database
    if (!teamAnalytics || teamAnalytics.length === 0) return [];

    // Calculate average productivity score as utilization
    const avgProductivity = teamAnalytics.reduce((sum, member) =>
      sum + (member.productivityScore || 0), 0) / teamAnalytics.length;

    const utilizedValue = Math.round(avgProductivity);
    const availableValue = 100 - utilizedValue;

    return [
      { name: "Utilized", value: utilizedValue },
      { name: "Available", value: availableValue },
    ];
  };

  const chartData = getChartData();

  // Calculate totals for display
  const totalCapacity = teamAnalytics?.reduce((sum, member) =>
    sum + (member.filesProcessed * 8 + member.transactionsReviewed * 0.5), 0) || 0;
  const totalBilled = teamAnalytics?.reduce((sum, member) =>
    sum + (member.filesProcessed * 7.5 + member.transactionsReviewed * 0.4), 0) || 0;

  const utilizationPercentage = chartData.find(item => item.name === "Utilized")?.value || 0;

  return (
    <div className="bg-card p-6 rounded-2xl border">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-brand-primary">Team Utilization</h3>
          <RealtimeStatusIndicator variant="icon" size="sm" />
          {isLoading && (
            <Badge variant="secondary" className="text-xs animate-pulse">
              Updating...
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="btn-swiss-secondary h-7 text-[10px]"
            onClick={() => setSelectedPeriod(selectedPeriod === "week" ? "month" : "week")}
          >
            This {selectedPeriod === "week" ? "Week" : "Month"} <i className="fa-solid fa-caret-down ml-1" />
          </Button>
          <button
            onClick={refresh}
            className="btn-swiss-secondary h-7 px-2 py-1.5 rounded-[7px] text-[10px] leading-[10px] hover:bg-brand-secondary transition-colors"
            title="Refresh data"
          >
            <i className="fa-solid fa-refresh" />
          </button>
        </div>
      </div>

      {chartData.length === 0 ? (
        // Empty state
        <div className="h-[150px] flex items-center justify-center text-center">
          <div className="text-muted-foreground">
            <i className="fa-solid fa-users fa-2x mb-4" />
            <p className="text-lg font-medium mb-2">No Team Data</p>
            <p className="text-sm">
              Team utilization metrics will appear here once you have team activity data.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="h-[150px] -mt-4 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="60%"
                  startAngle={180}
                  endAngle={0}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Center Text */}
            <div className="absolute inset-0 flex items-center justify-center pt-4">
              <div className="text-center">
                <div className="text-3xl font-space-grotesk font-bold text-brand-primary">
                  {utilizationPercentage}%
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-2">
            <p className="text-xs text-muted-foreground">
              Capacity: {Math.round(totalCapacity)}h / Billed: {Math.round(totalBilled)}h
            </p>
            {teamAnalytics && teamAnalytics.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {teamAnalytics.length} team member{teamAnalytics.length !== 1 ? 's' : ''} tracked
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};